// ============================================================================
// AUTH CONTEXT — Central auth state with memoized permission engine
// ============================================================================
// Provides auth state, permission engine, and scope management to all components.
// Supports both legacy mode (REACT_APP_AUTH_MODE=legacy) and tenant-hub mode.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { authService } from '../api/services/authService';
import { myPermissionsService } from '../api/services/myPermissionsService';
import { tokenManager } from '../auth/tokenManager';
import { STORAGE_KEYS } from '../auth/storageKeys';
import {
  createPermissionEngine,
  buildNavigationFromPermissions,
  buildModulesConfigFromPermissions,
} from '../auth/permissionEngine';
import type { PermissionEngine } from '../auth/permissionEngine';
import type {
  UserPermissionContext,
  UserScopeConfiguration,
  DiscoverResponse,
  AccessibleEntity,
  AccessibleBusinessUnit,
  AccessibleFinancialYear,
} from '../api/types/auth.types';

const getPermissionSource = (): 'tenant-hub' | 'local' => {
  return (process.env.REACT_APP_PERMISSION_SOURCE as 'tenant-hub' | 'local') || 'local';
};

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  userId: string;
  userName: string;
  userEmail: string;
  firstName: string;
  lastName: string;
  fullName: string;
  userRole: string;
}

/**
 * Tenant branding info — captured from the authenticate response.
 * Drives the header logo (LogoComponent) and the browser tab title.
 * `tenantLogoBase64` holds the raw base64 string (no `data:` prefix);
 * consumers prepend `data:image/png;base64,` when using as an <img> src.
 * Mirrors the react-project-template F3 shape verbatim so the LogoComponent
 * port drops in without shape adjustments.
 */
export interface TenantInfo {
  tenantName: string | null;
  tenantDisplayName: string | null;
  tenantLogoBase64: string | null;
  /** TRUE when the uploaded logo already embeds the tenant name (wordmark).
   *  LogoComponent hides the adjacent tenant-name text to avoid duplication. */
  logoContainsName: boolean;
}

export interface AuthContextValue {
  // === State ===
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  tenantContext: { tenantId: string; appId: string } | null;
  /** Tenant branding info — captured from the authenticate response.
   *  Drives the header logo (LogoComponent) and the browser tab title.
   *  `tenantLogoBase64` holds the raw base64 string (no `data:` prefix);
   *  consumers prepend `data:image/png;base64,` when using as an <img> src.
   *  Mirrors the react-project-template F3 shape. */
  tenantInfo: TenantInfo | null;
  permissionContext: UserPermissionContext | null;
  scopeConfiguration: UserScopeConfiguration | null;

  // === Auth Actions ===
  discover: (
    username: string,
    password: string,
    clientId?: string
  ) => Promise<DiscoverResponse>;
  authenticate: (
    username: string,
    password: string,
    tenantId: string,
    appId: string
  ) => Promise<void>;
  logout: () => void;
  refreshPermissions: () => Promise<void>;

  /**
   * Refetch the FBP-side permission context from `/api/v1/me/permissions`
   * with the currently-selected scope headers. Updates `permissionContext`
   * and `scopeConfiguration` in place. Called after a scope switch so
   * per-entity role permissions (which can differ between entities) are
   * reflected in downstream `useCan()` gates and the sidebar rebuild
   * effect that watches `permissionContext`.
   *
   * Distinct from `refreshPermissions()` above, which hits tenant-hub's
   * cache-invalidate endpoint and returns tenant-hub's own permission shape
   * (empty for FBP tenants — their real permissions live in the tenant DB).
   */
  reloadPermissionContext: () => Promise<void>;

  // === Permission Engine (O(1) lookups) ===
  permissionEngine: PermissionEngine;

  // === Scope Management ===
  selectedEntity: { id: string; name: string } | null;
  selectedBusinessUnit: { id: string; name: string } | null;
  selectedFinancialYear: { id: string; name: string; code: string } | null;
  setSelectedEntity: (id: string, name: string) => void;
  setSelectedBusinessUnit: (id: string, name: string) => void;
  /**
   * Branch-first setter (design 2026-07): atomically sets the selected Branch
   * (BU) AND its parent Entity in one call. Prefer this over the individual
   * setSelectedEntity / setSelectedBusinessUnit setters for user-driven scope
   * changes — the app's operational primary is the Branch; the Entity is
   * derived context stored on each Branch. Also writes SELECTED_ENTITY_* /
   * SELECTED_BU_* keys to localStorage so the axios interceptor picks up
   * X-Entity-ID and X-Business-Unit-ID headers atomically.
   */
  setSelectedBranch: (buId: string, buName: string, entityId: string, entityName: string) => void;
  setSelectedFinancialYear: (id: string, name: string, code: string) => void;
  accessibleEntities: AccessibleEntity[];
  accessibleBusinessUnits: AccessibleBusinessUnit[];
  accessibleFinancialYears: AccessibleFinancialYear[];
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Auth Mode Helper
// ============================================================================

const getAuthMode = (): 'tenant-hub' | 'legacy' => {
  return (process.env.REACT_APP_AUTH_MODE as 'tenant-hub' | 'legacy') || 'legacy';
};

// ============================================================================
// Provider
// ============================================================================

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenantContext, setTenantContext] = useState<{ tenantId: string; appId: string } | null>(null);
  // Tenant branding — hydrated from localStorage on mount so a page reload
  // shows the tenant name / logo instantly without re-hitting tenant-hub.
  // Refreshed on every successful authenticate() call.
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(() => {
    const raw = localStorage.getItem(STORAGE_KEYS.TENANT_INFO);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as TenantInfo;
    } catch {
      // Corrupt payload — clear it so we don't keep failing to parse.
      localStorage.removeItem(STORAGE_KEYS.TENANT_INFO);
      return null;
    }
  });
  const [permissionContext, setPermissionContext] = useState<UserPermissionContext | null>(null);
  const [scopeConfiguration, setScopeConfiguration] = useState<UserScopeConfiguration | null>(null);

  // Scope selections
  const [selectedEntity, setSelectedEntityState] = useState<{ id: string; name: string } | null>(null);
  const [selectedBusinessUnit, setSelectedBUState] = useState<{ id: string; name: string } | null>(null);
  const [selectedFinancialYear, setSelectedFYState] = useState<{
    id: string;
    name: string;
    code: string;
  } | null>(null);

  // Permission engine rebuilt ONLY when permissionContext changes
  const permissionEngine = useMemo(
    () => createPermissionEngine(permissionContext),
    [permissionContext]
  );

  // In tenant-hub mode, derive from token state (reactive via user/tenantContext deps).
  // In legacy mode, always return true — legacy login manages its own auth state
  // via localStorage, and route protection is handled by ProtectedRoute passthrough.
  const isAuthenticated = useMemo(() => {
    if (getAuthMode() === 'tenant-hub') {
      return !!user && tokenManager.isAuthenticated();
    }
    // Legacy mode: not managed by AuthContext — always return true
    return true;
  }, [user]);

  // === Session Recovery on Mount ===
  useEffect(() => {
    const recoverSession = () => {
      try {
        if (getAuthMode() === 'tenant-hub') {
          const token = tokenManager.getAccessToken();
          if (token && !tokenManager.isTokenExpired(token)) {
            // Hydrate state from localStorage
            const storedPermissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
            if (storedPermissions) {
              setPermissionContext(JSON.parse(storedPermissions));
            }

            const storedScope = localStorage.getItem(STORAGE_KEYS.SCOPE_CONFIG);
            if (storedScope) {
              const scope = JSON.parse(storedScope) as UserScopeConfiguration;
              setScopeConfiguration(scope);
            }

            // Recover user info
            const userName = localStorage.getItem(STORAGE_KEYS.USER_NAME);
            const userEmail = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
            if (userName) {
              setUser({
                userId: tokenManager.getUserId() || '',
                userName: userName,
                userEmail: userEmail || '',
                firstName: localStorage.getItem(STORAGE_KEYS.USER_FIRST_NAME) || '',
                lastName: localStorage.getItem(STORAGE_KEYS.USER_LAST_NAME) || '',
                fullName: localStorage.getItem(STORAGE_KEYS.FULL_NAME) || userName,
                userRole: localStorage.getItem(STORAGE_KEYS.USER_ROLE) || '',
              });
            }

            // Recover tenant context
            const tenantId = tokenManager.getTenantId();
            const appId = tokenManager.getAppId();
            if (tenantId && appId) {
              setTenantContext({ tenantId, appId });
            }

            // Recover scope selections. Fall back to the primary/first
            // accessible entity/BU/FY from the persisted permission_context
            // when the SELECTED_*_ID localStorage entries are missing —
            // this happens when the bundle-version check evicted stale
            // selections OR when the initial login persisted permissions
            // but skipped the scope-selection writes for any reason. The
            // axios interceptor reads SELECTED_ENTITY_ID directly, so we
            // MUST write back to localStorage here — not just to state —
            // or the very next request goes out without X-Entity-ID.
            const parsedScope: UserScopeConfiguration | null = storedPermissions
              ? (JSON.parse(storedPermissions).scopeConfiguration as UserScopeConfiguration) ?? null
              : null;

            let entityId = localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY_ID);
            let entityName = localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY_NAME);
            if ((!entityId || !entityName) && parsedScope?.accessibleEntities?.length) {
              const primary =
                parsedScope.accessibleEntities.find((e) => e.isPrimary) ||
                parsedScope.accessibleEntities[0];
              entityId = primary.id;
              entityName = primary.entityName;
              localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_ID, entityId);
              localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_NAME, entityName);
            }
            if (entityId && entityName) {
              setSelectedEntityState({ id: entityId, name: entityName });
            }

            let buId = localStorage.getItem(STORAGE_KEYS.SELECTED_BU_ID);
            let buName = localStorage.getItem(STORAGE_KEYS.SELECTED_BU_NAME);
            if ((!buId || !buName) && parsedScope?.accessibleBusinessUnits?.length) {
              const primary =
                parsedScope.accessibleBusinessUnits.find((b) => b.isPrimary) ||
                parsedScope.accessibleBusinessUnits[0];
              buId = primary.id;
              buName = primary.buName;
              localStorage.setItem(STORAGE_KEYS.SELECTED_BU_ID, buId);
              localStorage.setItem(STORAGE_KEYS.SELECTED_BU_NAME, buName);
            }
            if (buId && buName) {
              setSelectedBUState({ id: buId, name: buName });
            }

            let fyId = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_ID);
            let fyName = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_NAME);
            let fyCode = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_CODE);
            if ((!fyId || !fyName) && parsedScope?.accessibleFinancialYears?.length) {
              const current =
                parsedScope.accessibleFinancialYears.find((f) => f.isPrimary) ||
                parsedScope.accessibleFinancialYears.find((f) => f.isCurrent) ||
                parsedScope.accessibleFinancialYears[0];
              fyId = current.id;
              fyName = current.fyName;
              fyCode = current.fyCode;
              localStorage.setItem(STORAGE_KEYS.SELECTED_FY_ID, fyId);
              localStorage.setItem(STORAGE_KEYS.SELECTED_FY_NAME, fyName);
              localStorage.setItem(STORAGE_KEYS.SELECTED_FY_CODE, fyCode);
            }
            if (fyId && fyName) {
              setSelectedFYState({ id: fyId, name: fyName, code: fyCode || '' });
            }
          } else if (tokenManager.getRefreshToken()) {
            // Attempt silent refresh
            authService
              .refreshTenantHubTokens()
              .then(() => recoverSession())
              .catch(() => {
                tokenManager.clearAll();
              });
          }
        }
      } catch {
        // Silently fail on recovery errors
      } finally {
        setIsLoading(false);
      }
    };

    recoverSession();
  }, []);

  // === Auth Actions ===

  const discover = useCallback(
    async (
      username: string,
      password: string,
      clientId?: string
    ): Promise<DiscoverResponse> => {
      const response = await authService.discover({
        username,
        password,
        clientId,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Discovery failed');
      }

      return response.data;
    },
    []
  );

  const authenticate = useCallback(
    async (
      username: string,
      password: string,
      tenantId: string,
      appId: string
    ): Promise<void> => {
      // Clear stale session data from any previous app/login
      tokenManager.clearAll();

      const response = await authService.authenticate({
        username,
        password,
        selectedTenantId: tenantId,
        selectedApplicationId: appId,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Authentication failed');
      }

      const authData = response.data;
      setTenantContext({ tenantId, appId });

      // Capture tenant branding info — drives header logo + browser tab title.
      // Backend returns base64 without the `data:` prefix; the LogoComponent
      // prepends it when building the <img> src. Mirrors the F3 pattern from
      // react-project-template so behaviour stays consistent across scaffolds.
      const tInfo: TenantInfo = {
        tenantName:        authData.tenantName,
        tenantDisplayName: authData.tenantDisplayName,
        tenantLogoBase64:  authData.tenantLogoBase64,
        logoContainsName:  authData.tenantLogoContainsName ?? false,
      };
      setTenantInfo(tInfo);
      localStorage.setItem(STORAGE_KEYS.TENANT_INFO, JSON.stringify(tInfo));

      // ============================================================
      // Post-migration RBAC contract: tenant-hub is responsible ONLY
      // for identity + token + tenant-connection routing. Everything
      // RBAC-related (roles, scope, menus, permissions) is owned by
      // the tenant DB and served through FBP's own /me/permissions
      // endpoint.
      //
      // Consequently we DELIBERATELY IGNORE `authData.permissions`
      // and `authData.userRole` — they're a leaky abstraction from
      // the pre-migration era. Any FBP UI that displays "the user's
      // role" must derive it from `permissionContext.roles` (which
      // comes from iam.tbl_user_role in the tenant DB).
      //
      // /me/permissions failure is FATAL — a user who authenticates
      // against tenant-hub but has no effective RBAC configuration
      // in the tenant DB cannot use FBP. Better to fail loud at
      // login than to strand them in a broken screen.
      // ============================================================
      let fbPermissions;
      try {
        const fbResponse = await myPermissionsService.getMyPermissions();
        if (!fbResponse.success || !fbResponse.data) {
          throw new Error(fbResponse.message || 'Empty permissions response');
        }
        fbPermissions = fbResponse.data;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AuthContext] /me/permissions failed — user has no effective FBP access', err);
        tokenManager.clearAll();
        setTenantContext(null);
        setTenantInfo(null);
        localStorage.removeItem(STORAGE_KEYS.TENANT_INFO);
        throw new Error(
          'You are authenticated but have no permissions configured for this application. Please contact your administrator.'
        );
      }

      // Derive the display-role from the tenant DB, NOT tenant-hub.
      const primaryRole = fbPermissions.roles?.[0];
      const displayRole = primaryRole?.name || primaryRole?.code || '';

      setUser({
        userId: authData.userId || '',
        userName: authData.userName || '',
        userEmail: authData.userEmail || '',
        firstName: authData.userFirstName || '',
        lastName: authData.userLastName || '',
        fullName: [authData.userFirstName, authData.userMiddleName, authData.userLastName]
          .filter(Boolean)
          .join(' '),
        userRole: displayRole,
      });

      setPermissionContext(fbPermissions);
      setScopeConfiguration(fbPermissions.scopeConfiguration);
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(fbPermissions));

      // When PERMISSION_SOURCE=tenant-hub, store navigation and modules from
      // the tenant DB (via /me/permissions). When PERMISSION_SOURCE=local,
      // login-interface.tsx stores the local config instead.
      if (getPermissionSource() === 'tenant-hub') {
        const navSections = buildNavigationFromPermissions(fbPermissions);
        localStorage.setItem(STORAGE_KEYS.NAVIGATION_MENU, JSON.stringify(navSections));

        const modulesConfig = buildModulesConfigFromPermissions(fbPermissions);
        localStorage.setItem(STORAGE_KEYS.MODULES_CONFIG, JSON.stringify(modulesConfig));
      }

      // Hydrate scope selections from the tenant DB's scopeConfiguration.
      // Persist to localStorage so the axios interceptor (which reads
      // SELECTED_*_ID from localStorage on every request) has the correct
      // X-Entity-ID / X-Business-Unit-ID / X-Financial-Year-ID headers
      // immediately after login — without waiting for the user to manually
      // pick from the header dropdown.
      const scope = fbPermissions.scopeConfiguration;
      if (scope) {
        const primaryEntity =
          scope.accessibleEntities?.find((e) => e.isPrimary) ||
          scope.accessibleEntities?.[0];
        if (primaryEntity) {
          setSelectedEntityState({ id: primaryEntity.id, name: primaryEntity.entityName });
          localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_ID, primaryEntity.id);
          localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_NAME, primaryEntity.entityName);
        }

        const primaryBU =
          scope.accessibleBusinessUnits?.find((b) => b.isPrimary) ||
          scope.accessibleBusinessUnits?.[0];
        if (primaryBU) {
          setSelectedBUState({ id: primaryBU.id, name: primaryBU.buName });
          localStorage.setItem(STORAGE_KEYS.SELECTED_BU_ID, primaryBU.id);
          localStorage.setItem(STORAGE_KEYS.SELECTED_BU_NAME, primaryBU.buName);
        }

        const currentFY =
          scope.accessibleFinancialYears?.find((f) => f.isPrimary) ||
          scope.accessibleFinancialYears?.find((f) => f.isCurrent) ||
          scope.accessibleFinancialYears?.[0];
        if (currentFY) {
          setSelectedFYState({ id: currentFY.id, name: currentFY.fyName, code: currentFY.fyCode });
          localStorage.setItem(STORAGE_KEYS.SELECTED_FY_ID, currentFY.id);
          localStorage.setItem(STORAGE_KEYS.SELECTED_FY_NAME, currentFY.fyName);
          localStorage.setItem(STORAGE_KEYS.SELECTED_FY_CODE, currentFY.fyCode);
        }
      }

      // Legacy USER localStorage entry — role now comes from tenant DB.
      localStorage.setItem(
        STORAGE_KEYS.USER,
        JSON.stringify({
          id: authData.userId,
          username: authData.userName,
          email: authData.userEmail,
          role: displayRole,
        })
      );
    },
    []
  );

  const logout = useCallback(() => {
    if (getAuthMode() === 'tenant-hub') {
      authService.tenantHubLogout();
    } else {
      tokenManager.clearAll();
    }
    setUser(null);
    setTenantContext(null);
    setTenantInfo(null);
    localStorage.removeItem(STORAGE_KEYS.TENANT_INFO);
    setPermissionContext(null);
    setScopeConfiguration(null);
    setSelectedEntityState(null);
    setSelectedBUState(null);
    setSelectedFYState(null);
  }, []);

  const refreshPermissions = useCallback(async () => {
    const appId = tokenManager.getAppId();
    if (!appId) return;

    const response = await authService.refreshPermissions(appId);
    if (response.success && response.data) {
      setPermissionContext(response.data);
      setScopeConfiguration(response.data.scopeConfiguration);
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(response.data));
    }
  }, []);

  // Refetch FBP's /me/permissions with the currently-selected scope headers.
  // Mirrors the tail of the tenant-hub-mode authenticate() flow (lines
  // 332-376) so per-entity role permissions land in permissionContext +
  // localStorage without a full page reload. main_layout's effect that
  // watches permissionContext rebuilds NAVIGATION_MENU / MODULES_CONFIG
  // and dynamicNavigation from the fresh tree automatically.
  const reloadPermissionContext = useCallback(async () => {
    try {
      const fbResponse = await myPermissionsService.getMyPermissions();
      if (!fbResponse.success || !fbResponse.data) return;

      const fbPermissions = fbResponse.data;
      setPermissionContext(fbPermissions);
      setScopeConfiguration(fbPermissions.scopeConfiguration);
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(fbPermissions));

      if (getPermissionSource() === 'tenant-hub') {
        const navSections = buildNavigationFromPermissions(fbPermissions);
        localStorage.setItem(STORAGE_KEYS.NAVIGATION_MENU, JSON.stringify(navSections));

        const modulesConfig = buildModulesConfigFromPermissions(fbPermissions);
        localStorage.setItem(STORAGE_KEYS.MODULES_CONFIG, JSON.stringify(modulesConfig));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[reloadPermissionContext] failed', err);
      throw err;
    }
  }, []);

  // === Scope Setters ===

  const setSelectedEntity = useCallback((id: string, name: string) => {
    setSelectedEntityState({ id, name });
    localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_ID, id);
    localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_NAME, name);
  }, []);

  const setSelectedBusinessUnit = useCallback((id: string, name: string) => {
    setSelectedBUState({ id, name });
    localStorage.setItem(STORAGE_KEYS.SELECTED_BU_ID, id);
    localStorage.setItem(STORAGE_KEYS.SELECTED_BU_NAME, name);
  }, []);

  /**
   * Branch-first atomic setter — writes BU + Entity state and both localStorage
   * key pairs in one call. See the interface comment above for rationale.
   *
   * The FY resync effect in main_layout.tsx watches selectedEntity — when we
   * update entity here the effect fires and snaps FY to the new entity's
   * current FY if the previous FY doesn't belong to the new entity.
   */
  const setSelectedBranch = useCallback(
    (buId: string, buName: string, entityId: string, entityName: string) => {
      setSelectedEntityState({ id: entityId, name: entityName });
      setSelectedBUState({ id: buId, name: buName });
      localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_ID, entityId);
      localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_NAME, entityName);
      localStorage.setItem(STORAGE_KEYS.SELECTED_BU_ID, buId);
      localStorage.setItem(STORAGE_KEYS.SELECTED_BU_NAME, buName);
    },
    [],
  );

  const setSelectedFinancialYear = useCallback((id: string, name: string, code: string) => {
    setSelectedFYState({ id, name, code });
    localStorage.setItem(STORAGE_KEYS.SELECTED_FY_ID, id);
    localStorage.setItem(STORAGE_KEYS.SELECTED_FY_NAME, name);
    localStorage.setItem(STORAGE_KEYS.SELECTED_FY_CODE, code);
  }, []);

  // Accessible scope lists from scope configuration
  const accessibleEntities = scopeConfiguration?.accessibleEntities ?? [];
  const accessibleBusinessUnits = scopeConfiguration?.accessibleBusinessUnits ?? [];
  const accessibleFinancialYears = scopeConfiguration?.accessibleFinancialYears ?? [];

  // === Memoized Context Value ===
  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      tenantContext,
      tenantInfo,
      permissionContext,
      scopeConfiguration,
      discover,
      authenticate,
      logout,
      refreshPermissions,
      reloadPermissionContext,
      permissionEngine,
      selectedEntity,
      selectedBusinessUnit,
      selectedFinancialYear,
      setSelectedEntity,
      setSelectedBusinessUnit,
      setSelectedBranch,
      setSelectedFinancialYear,
      accessibleEntities,
      accessibleBusinessUnits,
      accessibleFinancialYears,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      tenantContext,
      tenantInfo,
      permissionContext,
      scopeConfiguration,
      discover,
      authenticate,
      logout,
      refreshPermissions,
      reloadPermissionContext,
      permissionEngine,
      selectedEntity,
      selectedBusinessUnit,
      selectedFinancialYear,
      setSelectedEntity,
      setSelectedBusinessUnit,
      setSelectedBranch,
      setSelectedFinancialYear,
      accessibleEntities,
      accessibleBusinessUnits,
      accessibleFinancialYears,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
